import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface SplashScreenProps {
  onComplete?: () => void;
  duration?: number;
}

const SplashScreen = ({ onComplete, duration = 2500 }: SplashScreenProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isLandscape, setIsLandscape] = useState(false);

  useEffect(() => {
    // Check orientation using rAF to avoid forced reflows
    let rafId: number;
    const checkOrientation = () => {
      rafId = requestAnimationFrame(() => {
        setIsLandscape(window.innerWidth > window.innerHeight);
      });
    };
    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    
    return () => {
      window.removeEventListener("resize", checkOrientation);
      cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        const increment = prev < 30 ? 15 : prev < 60 ? 10 : prev < 90 ? 5 : 2;
        return Math.min(prev + increment, 100);
      });
    }, duration / 15);

    const timer = setTimeout(() => {
      setProgress(100);
      setTimeout(() => {
        setIsVisible(false);
        onComplete?.();
      }, 300);
    }, duration);

    return () => {
      clearTimeout(timer);
      clearInterval(progressInterval);
    };
  }, [duration, onComplete]);

  // Use the branded splash images
  const splashImage = isLandscape 
    ? "/splash/splash-landscape.png" 
    : "/splash/splash-portrait.png";

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
        >
          {/* Background splash image */}
          <motion.div
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 bg-primary"
          >
            <img
              src={splashImage}
              alt="BailBuddies"
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to gradient if image fails to load
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </motion.div>

          {/* Overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />

          {/* Loading progress at bottom */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="absolute bottom-12 left-1/2 -translate-x-1/2 w-64 md:w-80"
          >
            <div className="relative h-1.5 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
              <motion.div
                className="absolute inset-y-0 left-0 bg-white rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              />
            </div>
            <motion.div
              className="mt-3 text-center text-sm text-white/80 font-medium"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <span className="tabular-nums">{Math.round(progress)}%</span>
              <span className="ml-2 text-xs opacity-70">BailBuddies is loading!</span>
            </motion.div>
          </motion.div>

          {/* Floating emojis - use CSS viewport units instead of JS window dimensions */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {["👻", "✨", "💤", "🛋️", "🌙", "👻"].map((emoji, i) => (
              <motion.div
                key={i}
                className="absolute text-3xl"
                style={{
                  left: `${(i * 15 + 10) % 100}%`,
                }}
                initial={{
                  y: "100vh",
                  rotate: i * 60,
                  opacity: 0.7,
                }}
                animate={{ y: "-10vh", rotate: (i + 3) * 60 }}
                transition={{ duration: 3 + (i % 3) * 0.5, delay: i * 0.3, ease: "easeOut" }}
              >
                {emoji}
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SplashScreen;
